/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';

import { Patterns } from '../../../../../Shared/Constants';
import { Icons } from '../../../../../Shared/Constants/Icons';
import theme from '../../../../../Shared/Themes/theme';
import { ErrorMsg } from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import {
  CardTitle,
  CloseDialogBoxIcon,
  buttonStyles,
} from '../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import { DatePickerContainer } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import { DialogWrapper, buttonStyle } from '../../../../Vendor/Components/CreateBid.Style';
import { BillingInfo } from '../../../Models/BidApproval.Model';

interface BillingInfoPopupProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: (billingInfo: BillingInfo) => void;
  defaultValues: BillingInfo;
  isSubmitting?: boolean;
}

const EMPTY_BILLING_INFO: BillingInfo = {
  companyName: '',
  billingContactFirstName: '',
  billingContactLastName: '',
  billingContactEmail: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
};

const BillingInfoPopup: React.FC<BillingInfoPopupProps> = ({
  open,
  onCancel,
  onConfirm,
  defaultValues,
  isSubmitting = false,
}) => {
  const { t } = useTranslation(['altTexts']);
  const { confirmButton, buttonText } = buttonStyles;
  const { secondaryButton } = buttonStyle;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BillingInfo>({ defaultValues: EMPTY_BILLING_INFO, mode: 'onChange' });

  // Re-populate the form whenever the popup is opened (summary data loads asynchronously).
  useEffect(() => {
    if (open) {
      reset({ ...EMPTY_BILLING_INFO, ...defaultValues });
    }
  }, [open, defaultValues, reset]);

  const renderField = (
    name: keyof BillingInfo,
    label: string,
    options: {
      required?: boolean;
      pattern?: { value: RegExp; message: string };
    } = {}
  ) => {
    const { required = false, pattern } = options;
    return (
      <Box sx={{ width: '100%' }}>
        <Controller
          control={control}
          name={name}
          rules={{
            ...(required ? { required: `${label} is required` } : {}),
            ...(pattern ? { pattern } : {}),
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              fullWidth
              variant="outlined"
              label={
                <span>
                  {label}
                  {required ? <span className="mandatory-field">*</span> : null}
                </span>
              }
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              error={Boolean(error)}
              sx={{ ...buttonStyles.textField, width: '100%', marginRight: 0 }}
            />
          )}
        />
        <ErrorMsg>{errors?.[name]?.message ?? ''}</ErrorMsg>
      </Box>
    );
  };

  const submitBillingInfo = (data: BillingInfo) => {
    onConfirm(data);
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onCancel}
      PaperProps={{
        style: { width: '50rem', maxWidth: '50rem' },
      }}>
      <CloseDialogBoxIcon onClick={onCancel}>
        <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
      </CloseDialogBoxIcon>
      <CardTitle>Please confirm billing information below.</CardTitle>

      <DatePickerContainer>
        {renderField('companyName', 'Company Name')}
        {renderField('billingContactEmail', 'Billing Contact Email Address', {
          pattern: { value: Patterns.Email, message: 'Please enter a valid email address' },
        })}
      </DatePickerContainer>
      <DatePickerContainer>
        {renderField('billingContactFirstName', 'Billing Contact First Name')}
        {renderField('billingContactLastName', 'Billing Contact Last Name')}
      </DatePickerContainer>
      <DatePickerContainer>
        {renderField('addressLine1', 'Address Line 1')}
        {renderField('addressLine2', 'Address Line 2')}
      </DatePickerContainer>
      <DatePickerContainer>
        {renderField('city', 'City')}
        {renderField('state', 'State')}
      </DatePickerContainer>
      <DatePickerContainer>
        {renderField('zip', 'Zip', {
          pattern: { value: Patterns.OnlyInteger, message: 'Please enter a valid zip code' },
        })}
        <Box sx={{ width: '100%' }} />
      </DatePickerContainer>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '1.5rem', marginTop: '1em' }}>
        <Button sx={{ ...secondaryButton, flex: 1, marginRight: 0 }} onClick={onCancel} disabled={isSubmitting}>
          <Typography sx={{ ...buttonText, color: theme.palette.primary.dark }}>Cancel</Typography>
        </Button>
        <Button
          sx={{ ...confirmButton, flex: 1, marginTop: 0, opacity: isSubmitting ? 0.5 : 1 }}
          onClick={handleSubmit(submitBillingInfo)}
          disabled={isSubmitting}>
          {isSubmitting ? (
            <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
          ) : (
            <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>Confirm</Typography>
          )}
        </Button>
      </Box>
    </DialogWrapper>
  );
};

export default BillingInfoPopup;
